// PosReconciliationPanel.jsx — compares POS daily summary (dsum) against the
// invoice file (inv) on a per-day basis. Surfaces:
//   • Days where the two sources disagree by > 5% (potential upload gap).
//   • Aggregate gap (money + count).
// Renders nothing if either source is missing or both are perfectly aligned.
import React, { useMemo, useState } from "react";
import { fm } from "./lib/analytics";

const fmF = (n, d = 1) => (Number(n) || 0).toFixed(d);

const GAP_THRESHOLD_PCT = 5;

export default function PosReconciliationPanel({ inv, dsum }) {
  const [expanded, setExpanded] = useState(false);

  const rows = useMemo(() => {
    if (!inv?.length || !dsum?.length) return [];
    // Aggregate invoices per day
    const invByDate = {};
    for (const r of inv) {
      const d = (r.date || "").slice(0, 10);
      if (!d) continue;
      const e = invByDate[d] || (invByDate[d] = { date: d, inv_total: 0, inv_count: 0 });
      e.inv_total += Number(r.total) || 0;
      e.inv_count += 1;
    }
    // Pull POS rows
    const dsumByDate = {};
    for (const r of dsum) {
      const d = (r.date || "").slice(0, 10);
      if (!d) continue;
      dsumByDate[d] = {
        date: d,
        pos_total: Number(r.net_with_tax) || Number(r.net) || Number(r.gross) || 0,
        pos_count: Number(r.count) || 0,
        cancels: Number(r.cancel_count) || 0,
        returns: Number(r.return_count) || 0,
      };
    }
    // Merge sorted desc
    const all = Array.from(new Set([...Object.keys(invByDate), ...Object.keys(dsumByDate)])).sort();
    return all.map((d) => {
      const iv = invByDate[d] || { inv_total: 0, inv_count: 0 };
      const ps = dsumByDate[d] || { pos_total: 0, pos_count: 0, cancels: 0, returns: 0 };
      const gap_money = ps.pos_total - iv.inv_total;
      const gap_pct = ps.pos_total > 0 ? (gap_money / ps.pos_total) * 100 : (iv.inv_total > 0 ? -100 : 0);
      const gap_count = ps.pos_count - iv.inv_count;
      const both = iv.inv_total > 0 && ps.pos_total > 0;
      return {
        date: d,
        inv_total: iv.inv_total, inv_count: iv.inv_count,
        pos_total: ps.pos_total, pos_count: ps.pos_count,
        cancels: ps.cancels, returns: ps.returns,
        gap_money, gap_pct, gap_count,
        both,
      };
    });
  }, [inv, dsum]);

  const stats = useMemo(() => {
    const bothRows = rows.filter((r) => r.both);
    const totPos = bothRows.reduce((s, r) => s + r.pos_total, 0);
    const totInv = bothRows.reduce((s, r) => s + r.inv_total, 0);
    const gapMoney = totPos - totInv;
    const gapPct = totPos > 0 ? (gapMoney / totPos) * 100 : 0;
    const flagged = bothRows.filter((r) => Math.abs(r.gap_pct) > GAP_THRESHOLD_PCT);
    const onlyPos = rows.filter((r) => r.pos_total > 0 && r.inv_total === 0);
    const onlyInv = rows.filter((r) => r.inv_total > 0 && r.pos_total === 0);
    return { totPos, totInv, gapMoney, gapPct, flagged, onlyPos, onlyInv, daysOverlap: bothRows.length };
  }, [rows]);

  // Hide panel when either source is empty.
  if (!rows.length || !dsum?.length || !inv?.length) return null;

  const isHealthy = Math.abs(stats.gapPct) < 1 && stats.flagged.length === 0
                    && stats.onlyPos.length === 0 && stats.onlyInv.length === 0;

  return (
    <div className="rec-panel" data-testid="pos-reconciliation-panel">
      <button
        type="button"
        className="rec-head"
        onClick={() => setExpanded((v) => !v)}
        data-testid="rec-toggle"
        aria-expanded={expanded}
      >
        <div className="rec-head-l">
          <span className={`rec-status rec-status-${isHealthy ? "ok" : "warn"}`} aria-hidden>
            {isHealthy ? "✓" : "⚠"}
          </span>
          <div>
            <div className="rec-title">مطابقة POS مع الفواتير</div>
            <div className="rec-sub">
              {isHealthy
                ? `كل البيانات متطابقة عبر ${stats.daysOverlap} يوم`
                : `${stats.flagged.length + stats.onlyInv.length + stats.onlyPos.length} يوم يحتاج مراجعة من أصل ${rows.length}`}
            </div>
          </div>
        </div>
        <div className="rec-head-r">
          <div className="rec-kpi">
            <span className="rec-kpi-l">فرق إجمالي</span>
            <span className={`rec-kpi-v ${stats.gapMoney >= 0 ? "is-pos" : "is-neg"}`}>
              {stats.gapMoney >= 0 ? "+" : ""}{fmF(stats.gapMoney, 0)} ر
            </span>
          </div>
          <div className="rec-kpi">
            <span className="rec-kpi-l">نسبة</span>
            <span className={`rec-kpi-v ${Math.abs(stats.gapPct) > 1 ? "is-warn" : ""}`}>
              {stats.gapPct >= 0 ? "+" : ""}{fmF(stats.gapPct, 2)}%
            </span>
          </div>
          <span className={`rec-chev ${expanded ? "is-open" : ""}`} aria-hidden>▾</span>
        </div>
      </button>

      {expanded && (
        <div className="rec-body" data-testid="rec-body">
          {/* Top-line stats */}
          <div className="rec-stats-grid">
            <RecStat label="POS مجموع" value={`${fmF(stats.totPos, 0)} ر`} testid="rec-stat-pos" />
            <RecStat label="الفواتير مجموع" value={`${fmF(stats.totInv, 0)} ر`} testid="rec-stat-inv" />
            <RecStat label="أيام بفجوة > 5%" value={fm(stats.flagged.length)} flag={stats.flagged.length > 0} testid="rec-stat-flagged" />
            <RecStat label="أيام في POS فقط" value={fm(stats.onlyPos.length)} flag={stats.onlyPos.length > 0} testid="rec-stat-only-pos" />
            <RecStat label="أيام في الفواتير فقط" value={fm(stats.onlyInv.length)} flag={stats.onlyInv.length > 0} testid="rec-stat-only-inv" />
            <RecStat label="أيام متطابقة" value={fm(stats.daysOverlap - stats.flagged.length)} testid="rec-stat-ok" />
          </div>

          {/* Flagged days table */}
          {stats.flagged.length > 0 && (
            <div className="rec-section" data-testid="rec-flagged-section">
              <div className="rec-section-h">🚨 أيام بفجوة أكبر من 5% (تحتاج مراجعة)</div>
              <RecTable rows={stats.flagged} />
            </div>
          )}

          {stats.onlyPos.length > 0 && (
            <div className="rec-section">
              <div className="rec-section-h">📅 أيام موجودة في POS ومفقودة من ملف الفواتير</div>
              <ul className="rec-list">
                {stats.onlyPos.slice(0, 12).map((r) => (
                  <li key={r.date}>
                    <b>{r.date}</b> · {fmF(r.pos_total, 0)} ر · {fm(r.pos_count)} فاتورة
                  </li>
                ))}
                {stats.onlyPos.length > 12 && <li className="rec-list-more">…و {stats.onlyPos.length - 12} يوم آخر</li>}
              </ul>
              <div className="rec-tip">💡 ارفع ملف الفواتير المُحدّث لمعرفة تفاصيل هذه الأيام (الشفتات، التسجيل، إلخ).</div>
            </div>
          )}

          {stats.onlyInv.length > 0 && (
            <div className="rec-section">
              <div className="rec-section-h">📊 أيام موجودة في الفواتير ومفقودة من POS</div>
              <ul className="rec-list">
                {stats.onlyInv.slice(0, 12).map((r) => (
                  <li key={r.date}>
                    <b>{r.date}</b> · {fmF(r.inv_total, 0)} ر · {fm(r.inv_count)} فاتورة
                  </li>
                ))}
                {stats.onlyInv.length > 12 && <li className="rec-list-more">…و {stats.onlyInv.length - 12} يوم آخر</li>}
              </ul>
              <div className="rec-tip">💡 ارفع ملخص المبيعات اليومي المُحدّث ليصبح المصدر الأدق لكل الأرقام.</div>
            </div>
          )}

          {isHealthy && (
            <div className="rec-healthy">
              ✅ ممتاز! الإجمالي مطابق بفارق أقل من 1% عبر كل الأيام. لا يوجد خلل واضح.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RecStat({ label, value, flag, testid }) {
  return (
    <div className={`rec-stat ${flag ? "rec-stat-flag" : ""}`} data-testid={testid}>
      <div className="rec-stat-l">{label}</div>
      <div className="rec-stat-v">{value}</div>
    </div>
  );
}

function RecTable({ rows }) {
  return (
    <div className="rec-table-wrap">
      <table className="rec-table">
        <thead>
          <tr>
            <th>التاريخ</th>
            <th className="rc">POS</th>
            <th className="rc">الفواتير</th>
            <th className="rc">الفرق</th>
            <th className="rc">%</th>
            <th className="rc">إرجاع</th>
            <th className="rc">إلغاء</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.date} data-testid={`rec-row-${r.date}`}>
              <td><b>{r.date}</b></td>
              <td className="rc">{fmF(r.pos_total, 0)} ر</td>
              <td className="rc">{fmF(r.inv_total, 0)} ر</td>
              <td className={`rc ${r.gap_money >= 0 ? "is-pos" : "is-neg"}`}>
                {r.gap_money >= 0 ? "+" : ""}{fmF(r.gap_money, 0)} ر
              </td>
              <td className={`rc ${Math.abs(r.gap_pct) > 5 ? "is-warn" : ""}`}>
                {r.gap_pct >= 0 ? "+" : ""}{fmF(r.gap_pct, 1)}%
              </td>
              <td className="rc">{fm(r.returns)}</td>
              <td className="rc">{fm(r.cancels)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
